import { computed, ref, type Ref } from 'vue';

export function createShipmentFormDevicesSlice(generalName: Ref<string>) {
  const selectedDevices = ref<
    { id: number; label: string }[]
  >([]);
  const nameAutofillSourceDeviceId = ref<number | null>(null);
  const nameAutofillBaseline = ref<string | null>(null);
  const persistedDeviceIdsSnapshot = ref<string | null>(null);

  const primaryDeviceId = computed(() => selectedDevices.value[0]?.id ?? null);
  const selectedDeviceIds = computed(() =>
    selectedDevices.value.map((device) => device.id),
  );

  function deviceIdsSnapshot(deviceIds: readonly number[]): string {
    return JSON.stringify([...deviceIds].sort((a, b) => a - b));
  }

  function markDeviceIdsPersisted(deviceIds: readonly number[]) {
    persistedDeviceIdsSnapshot.value = deviceIdsSnapshot(deviceIds);
  }

  function haveDeviceIdsChangedSincePersisted(
    deviceIds?: readonly number[],
  ): boolean {
    const current = [...(deviceIds ?? selectedDeviceIds.value)];
    if (persistedDeviceIdsSnapshot.value == null) return current.length > 0;
    return deviceIdsSnapshot(current) !== persistedDeviceIdsSnapshot.value;
  }

  function setSelectedDevices(
    devices: { id: number; label: string }[],
  ): void {
    selectedDevices.value = devices.map((device) => ({ ...device }));
  }

  function clearSelectedDevices(): void {
    selectedDevices.value = [];
  }

  function isNameAutofillUntouched(): boolean {
    if (nameAutofillBaseline.value === null) return true;
    return generalName.value.trim() === nameAutofillBaseline.value.trim();
  }

  function setNameAutofillFromDevice(deviceId: number, name: string): void {
    generalName.value = name;
    nameAutofillSourceDeviceId.value = deviceId;
    nameAutofillBaseline.value = name;
  }

  function clearNameAutofillState(): void {
    nameAutofillSourceDeviceId.value = null;
    nameAutofillBaseline.value = null;
  }

  function resetDevices() {
    clearSelectedDevices();
    clearNameAutofillState();
    persistedDeviceIdsSnapshot.value = null;
  }

  return {
    selectedDevices,
    nameAutofillSourceDeviceId,
    nameAutofillBaseline,
    persistedDeviceIdsSnapshot,
    primaryDeviceId,
    selectedDeviceIds,
    markDeviceIdsPersisted,
    haveDeviceIdsChangedSincePersisted,
    setSelectedDevices,
    clearSelectedDevices,
    isNameAutofillUntouched,
    setNameAutofillFromDevice,
    clearNameAutofillState,
    resetDevices,
  };
}
